<template>
  <Dialog :commonDialog="documentDialog" :dialog-width="800">
    <template v-slot:title>Add Incident Report</template>
    <template v-slot:body>
      <v-form ref="incidentForm" v-model="formValid">
        <v-container>
          <v-row>
            <!-- Type Radio Buttons -->
            <v-col cols="12">
              <label class="font-weight-600 font-size-18 required">Type</label>
              <RadioInputCustom
                v-model="formData.type"
                row
                :items="[
                  {
                    label: 'Positive',
                    color: 'green darken-3 white--text',
                    value: 'positive',
                  },
                  {
                    label: 'Negative',
                    color: 'red darken-4 white--text',
                    value: 'negative',
                  },
                ]"
                :rules="[validateRules.required(formData.type, 'Type')]"
              />
            </v-col>

            <v-col cols="6">
              <label class="font-weight-600 font-size-18 required"
                >Customer</label
              >
              <v-autocomplete
                :disabled="formLoading"
                :loading="formLoading"
                :items="customerList"
                v-model="formData.customer"
                hide-details
                clearable
                flat
                solo
                dense
                item-value="id"
                item-text="display_name"
                label="Customer"
                color="cyan"
                item-color="cyan"
                class="pt-0 mt-0 mr-4"
                :rules="[validateRules.required(formData.customer, 'Customer')]"
              >
              </v-autocomplete>
            </v-col>

            <!-- Date Picker -->
            <v-col cols="6">
              <label class="font-weight-600 font-size-18 required">Date</label>
              <DatePicker
                v-model="formData.incident_date"
                :rules="[
                  validateRules.required(
                    formData.incident_date,
                    'Incident Date'
                  ),
                ]"
              ></DatePicker>
            </v-col>

            <!-- Amount Damage -->
            <v-col cols="6">
              <label class="font-weight-600 font-size-18">Amount Damage</label>
              <v-text-field
                v-model.number="formData.amount_damage"
                type="number"
                label="Amount Damage"
                dense
                filled
                solo
                flat
              ></v-text-field>
            </v-col>

            <!-- Tags -->
            <v-col cols="6">
              <label class="font-weight-600 font-size-18">Tags</label>
              <tag-auto-complete
                :type="tag_type"
                v-model="formData.tag"
                placeholder="Tags"
              />
            </v-col>

            <!-- Remark -->
            <v-col cols="12">
              <label class="font-weight-600 font-size-18">Remark</label>
              <TextAreaField
                auto-grow
                dense
                filled
                color="cyan"
                v-model.trim="formData.remark"
                placeholder="Remarks"
                solo
                flat
                row-height="25"
                counter="150"
              />
            </v-col>

            <!-- Attachments -->
            <v-col cols="12">
              <DocumnetList
                label="Attachments"
                v-model="formData.attachments"
                :document-type="3"
                tag-type="incident"
              ></DocumnetList>
            </v-col>
            <!-- Rating -->
            <v-col cols="12">
              <label class="font-weight-600 font-size-18 required d-flex"
                >Rating
              </label>
              <v-rating
                v-model="formData.rating"
                background-color="orange lighten-3"
                color="orange"
                label="Rating"
                size="60"
                style="max-width: 100%"
                :length="10"
              ></v-rating>

              <span class="justify-center d-flex">
                <h5>Rating: {{ formData.rating }}</h5>
              </span>
            </v-col>

            <!-- Show on Mobile -->
            <v-col cols="12">
              <v-checkbox
                v-model="formData.show_on_mobile"
                label="Show on Mobile App"
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </template>
    <template v-slot:action>
      <v-btn
        depressed
        color="cyan"
        class="white--text"
        :loading="formLoading"
        :disabled="!formValid || formLoading"
        @click="submitForm"
      >
        Save
      </v-btn>
      <v-btn depressed class="mx-2" :disabled="formLoading" @click="cancel()">
        Cancel
      </v-btn>
    </template>
  </Dialog>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog";
import DocumnetList from "@/view/pages/DocumnetList";
import DatePicker from "@/view/components/QDatePicker";
import ValidationMixin from "@/core/plugins/validation-mixin";
import { POST, GET } from "@/core/services/store/request.module";
import RadioInputCustom from "@/view/components/RadioInputCustom.vue";
import TagAutoComplete from "@/view/components/TagAutoCompleteInput.vue";

export default {
  name: "InternalIncidentReport",
  mixins: [ValidationMixin],
  components: {
    Dialog,
    DocumnetList,
    DatePicker,
    RadioInputCustom,
    "tag-auto-complete": TagAutoComplete,
  },
  props: {
    typeId: {
      type: Number,
      default: 0,
    },
    documentDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formValid: false,
      formLoading: false,
      customerList: [],
      tag_type: "engineer-incidents-report",
      formData: {
        type: "positive",
        customer: null,
        incident_date: null,
        amount_damage: null,
        tag: [],
        attachments: [],
        remark: null,
        rating: 10,
        show_on_mobile: false,
      },
    };
  },
  watch: {
    documentDialog: {
      handler(param) {
        if (param) {
          this.getAllCustomers();
        }
      },
      immediate: true,
    },
  },
  methods: {
    resetForm() {
      this.formData = {
        type: "positive",
        customer: null,
        incident_date: null,
        amount_damage: null,
        tag: [],
        attachments: [],
        remark: null,
        rating: 10,
        show_on_mobile: false,
      };
      this.$refs.incidentForm.resetValidation();
    },
    submitForm() {
      if (!this.$refs.incidentForm.validate()) return;

      this.formLoading = true;
      this.$store
        .dispatch(POST, {
          url: `technician/incident/${this.typeId}`,
          data: this.formData,
        })
        .then(() => {
          this.$emit("success", true);
          this.resetForm();
          this.$emit("close");
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.formLoading = false;
        });
    },
    getAllCustomers() {
      this.formLoading = true;
      this.$store
        .dispatch(GET, { url: "customer-list" })
        .then(({ data }) => {
          this.customerList = data;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.formLoading = false;
        });
    },
    cancel() {
      this.resetForm();
      this.$emit("close");
    },
  },
  mounted() {
    this.getAllCustomers();
  },
};
</script>
